import { PlanByInvoice } from '../../types/plans';

/**
 * Get the time length from a plan.
 */
export const getTimeLength = (plan: PlanByInvoice) => {
  const monthsDiff = plan?.intervalLength;
  const yearsDiff = parseInt((monthsDiff / 12).toString(), 10);

  if (monthsDiff >= 12)
    return `${yearsDiff} ${yearsDiff > 1 ? 'years' : 'year'}`;
  return `${monthsDiff} ${monthsDiff > 1 ? 'months' : 'month'}`;
};

/**
 * Formats a date to the format "MMMM d, yyyy" in UTC.
 * @param {string | Date} date - The date to format. Can be a Date object or a string that can be parsed into a Date.
 * @returns {string} The formatted date.
 */
export function formatDate(date: string | Date) {
  const options: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  };
  return new Intl.DateTimeFormat('en-US', options).format(new Date(date));
}

/**
 * Formats a date to the format "MM/dd/yyyy" in UTC.
 * @param {string | Date} date - The date to format. Can be a Date object or a string that can be parsed into a Date.
 * @returns {string} The formatted date.
 */
export function formatDateShort(date: string | Date) {
  const options: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  };
  return new Intl.DateTimeFormat('en-US', options).format(new Date(date));
}

/**
 * Formats a date string to ISO 8601 format for Schema.org and Open Graph.
 */
export function formatSchemaDate(dateStr?: string | null): string | undefined {
  if (!dateStr) return undefined;

  try {
    const date = new Date(dateStr);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString();
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Matches the learn header date freshness rule.
 */
export const isWithinFiveYears = (
  dateStr?: string | null,
  currentDate = new Date(),
): boolean => {
  const year = new Date(dateStr ?? '').getFullYear();
  return !Number.isNaN(year) && currentDate.getFullYear() - year <= 5;
};

/**
 * Formats a date as ISO only when it passes the learn freshness rule.
 */
export function formatFreshSchemaDate(
  dateStr?: string | null,
  currentDate = new Date(),
): string | undefined {
  if (!isWithinFiveYears(dateStr, currentDate)) return undefined;
  return formatSchemaDate(dateStr);
}

/**
 * Returns the first ISO date from priority-ordered dates that pass the learn freshness rule.
 */
export function getPriorityFreshSchemaDate(
  dateStrs: Array<string | null | undefined>,
  currentDate = new Date(),
): string | undefined {
  const dateStr = dateStrs.find((candidate) =>
    isWithinFiveYears(candidate, currentDate),
  );

  return formatSchemaDate(dateStr);
}

/**
 * Subtracts a number of years from a date.s.
 */
export const subtractYears = (date = new Date(), years = 1) => {
  const newDate = new Date(date);
  newDate.setFullYear(newDate.getFullYear() - years);
  return newDate;
};
